import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import * as fromConfigurationStore from '@appStore/configuration';
import * as fromAnalyzeStore from '@appStore/analyze';
import * as fromContractsStore from '@appStore/contracts';
import * as fromAlarmsStore from '../../app/secure/alarms/store';
import * as fromMeteringPointStore from '@appStore/metering-points';
import * as fromCustomersStore from '@appStore/customers';
import * as fromManagersStore from '@appStore/managers';
import { Subject, Observable, Subscription, of } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client'; //'@core/auth/angular-auth-oidc-client';
import * as combinedSelectors from '@appStore/managers/combined-selectors';
import { switchMap, filter, shareReplay, take, takeUntil, tap, map } from 'rxjs/operators';
import { config } from 'process';

@Injectable({
  providedIn: 'root'
})
export class SxReloadPermissionService {
  selectedManagerCompanyId: number;

  constructor(
    private configStore: Store<fromConfigurationStore.State>,
    private analyzeStore: Store<fromAnalyzeStore.State>,
    private contractsStore: Store<fromContractsStore.State>,
    private alarmsStore: Store<fromAlarmsStore.State>,
    private meteringPointStore: Store<fromMeteringPointStore.MeteringPointsState>,
    private customerStore: Store<fromCustomersStore.CustomersState>,
    private managersStore: Store<fromManagersStore.State>,
    private oidcSecurityService: OidcSecurityService
  ) {}

  public reloadPermissionsForManager(): Observable<boolean> {
    let getManagerCompanySelectedDestroy = new Subject<void>();

    let isRefreshed$ = this.refreshSession();

    // Clear content in Store (Analyze, Contracts, Alarm)
    this.analyzeStore.dispatch(new fromAnalyzeStore.AnalyzeClearData());
    this.contractsStore.dispatch(new fromContractsStore.ContractClearData());
    this.alarmsStore.dispatch(new fromAlarmsStore.ClearData());

    this.configStore.dispatch(new fromConfigurationStore.ClearPermission());
    this.meteringPointStore.dispatch(new fromMeteringPointStore.ClearMeteringPointGroups());
    this.customerStore.dispatch(new fromCustomersStore.ClearCustomers());

    isRefreshed$
      .pipe(
        filter(isRefreshed => isRefreshed),        
        take(1),
        tap(_ => {
          this.configStore.dispatch(new fromConfigurationStore.LoadPermission());
          this.meteringPointStore.dispatch(new fromMeteringPointStore.LoadMeteringPoints());
          this.meteringPointStore.dispatch(new fromMeteringPointStore.LoadMeteringPointGroups());
          this.customerStore.dispatch(new fromCustomersStore.CustomersLoadData());
          this.managersStore.dispatch(new fromManagersStore.ManagerCompaniesLoadData());
        }),
        switchMap(x =>
          this.managersStore
            .select(fromManagersStore.getManagerCompanySelected)
            .pipe(takeUntil(getManagerCompanySelectedDestroy))
        )
      )
      .subscribe(managerCompany => {
        this.selectedManagerCompanyId = managerCompany ? managerCompany.id : 0;
        if (this.selectedManagerCompanyId !== 0) {
          this.managersStore.dispatch(
            new fromManagersStore.ManagerCompanyCustomersLoadData({
              managerCompanyId: this.selectedManagerCompanyId
            })
          );
        }

        getManagerCompanySelectedDestroy.next();
        getManagerCompanySelectedDestroy.complete();
      });

    return this.managersStore.select(combinedSelectors.getReloadPermissionsLoaded).pipe(filter(loaded => loaded));
  }

  public reloadPermissions(): Observable<boolean> {
     
    let isRefreshed$ = this.refreshSession();

    // Clear content in Store (Analyze, Contracts, Alarm)
    this.analyzeStore.dispatch(new fromAnalyzeStore.AnalyzeClearData());
    this.contractsStore.dispatch(new fromContractsStore.ContractClearData());
    this.alarmsStore.dispatch(new fromAlarmsStore.ClearData());

    this.configStore.dispatch(new fromConfigurationStore.ClearPermission());
    this.meteringPointStore.dispatch(new fromMeteringPointStore.ClearMeteringPointGroups());
    this.customerStore.dispatch(new fromCustomersStore.ClearCustomers());
    this.customerStore.dispatch(new fromCustomersStore.ClearCustomersUserAccessData());

    isRefreshed$
      .pipe(
        //filter(authResult => !!authResult && authResult?.allConfigsAuthenticated.some(cfg => cfg.isAuthenticated)),
        filter(isRefreshed => isRefreshed),        
        take(1)
      )
      .subscribe(x => {
        this.configStore.dispatch(new fromConfigurationStore.LoadPermission());
        this.meteringPointStore.dispatch(new fromMeteringPointStore.LoadMeteringPoints());
        this.meteringPointStore.dispatch(new fromMeteringPointStore.LoadMeteringPointGroups());
        this.customerStore.dispatch(new fromCustomersStore.CustomersLoadData());
        this.customerStore.dispatch(new fromCustomersStore.CustomersUserAccessLoadData());
      });

    return this.customerStore.select(combinedSelectors.getReloadPermissionsLoaded).pipe(filter(loaded => loaded));
  }

  private refreshSession(): Observable<boolean> {
    let authorizedConfigId$ = this.oidcSecurityService.isAuthenticated$
      .pipe(
        filter(authResult => !!authResult && authResult?.allConfigsAuthenticated.some(cfg => cfg.isAuthenticated)),
        map(authResult => authResult.allConfigsAuthenticated.find(cfg => cfg.isAuthenticated)),
        //tap(result => console.log(`reload-permission-service\trefreshSession\tisAuthenticated`, result)),
        map(configAuthResult => configAuthResult.configId),
        take(1),
      );

      return authorizedConfigId$.pipe(
        switchMap(configId => this.oidcSecurityService.forceRefreshSession(null, configId)),
        //tap(result => console.log(`reload-permission-service\trefreshSession\tforceRefreshSession`, result)),
        filter(result => !!result && result.isAuthenticated),
        take(1),
        map(result => result.isAuthenticated)
      );
  }
}
